import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import api, { qs, apiError } from "@/lib/api";
import { fmtINR, fmtDay } from "@/lib/service";
import { monthLabel } from "@/lib/dpc";
import { toast } from "sonner";
import { Target, Flame, Flag, Car, TrendingUp, TrendingDown, CalendarDays, Upload, ClipboardList } from "lucide-react";
import { ProratedBadge, PRORATED_TITLE } from "@/components/PlanBits";

export const TIER_STYLE = {
  platinum: { ring: "#7c3aed", chip: "bg-violet-600 text-white", label: "Platinum" },
  gold: { ring: "#d97706", chip: "bg-amber-400 text-amber-950", label: "Gold" },
  silver: { ring: "#64748b", chip: "bg-slate-300 text-slate-900", label: "Silver" },
  bronze: { ring: "#c2410c", chip: "bg-orange-300 text-orange-950", label: "Bronze" },
};
const ringColor = (pct) => (pct == null ? "#cbd5e1" : pct >= 110 ? TIER_STYLE.platinum.ring : pct >= 100 ? TIER_STYLE.gold.ring : pct >= 90 ? TIER_STYLE.silver.ring : pct >= 80 ? TIER_STYLE.bronze.ring : "#dc2626");
const short = (v) => (v >= 1e7 ? `${(v / 1e7).toFixed(2)} Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(2)} L` : fmtINR(Math.round(v)));
const nos = (v) => (Number.isInteger(v) ? fmtINR(v) : Number(v).toFixed(1));
export const money = (v, unit) => (unit === "nos" ? nos(v) : `₹${short(v)}`);

// Two flavours of the same panel: After-Sales revenue targets and Sales (DPC) plan achievement.
const SERVICE_CFG = {
  endpoint: "/service/targets/achievement", headline: "revenue", headlineLabel: "revenue", unit: "₹", cats: true, store: "svc-target-celebrated",
  celebrate: (d) => `${d.cat} revenue target`,
  empty: (d, cat, single) => `No ${cat === "TOTAL" ? "Total" : cat} target uploaded for this month${single ? " for this workshop" : ""}.`,
  manage: { to: "/service/targets", label: "Upload the monthly target workbook", icon: Upload, adminOnly: true },
};
const SALES_CFG = {
  endpoint: "/dpc/achievement", headline: "retail", headlineLabel: "retail plan", unit: "nos", cats: false, store: "dpc-target-celebrated",
  celebrate: () => "month retail plan",
  empty: (d) => `No plan with quantities for ${monthLabel(d.month)} yet.`,
  manage: { to: "/dpc/plans", label: "Open the plan board", icon: ClipboardList, adminOnly: false },
};

export const TierChip = ({ tier, size = "sm" }) => tier ? <span className={`rounded px-1.5 py-0.5 font-bold uppercase tracking-wider ${size === "sm" ? "text-[9px]" : "text-[11px]"} ${TIER_STYLE[tier].chip}`} data-testid={`tier-${tier}`}>{TIER_STYLE[tier].label}</span> : null;

function Ring({ m, meta }) {
  const pct = m.pct;
  const R = 26, C = 2 * Math.PI * R;
  const fill = pct == null ? 0 : Math.min(pct, 120) / 120;
  return (
    <div className="flex flex-col items-center text-center min-w-0" data-testid={`target-ring-${meta.key}`}>
      <div className="relative h-16 w-16">
        <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
          <circle cx="32" cy="32" r={R} fill="none" stroke="currentColor" className="text-secondary" strokeWidth="6" />
          <circle cx="32" cy="32" r={R} fill="none" stroke={ringColor(pct)} strokeWidth="6" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - fill)} style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)" }} />
          {/* 100 % tick at 5/6 of the ring */}
          <line x1="32" y1="2" x2="32" y2="8" stroke="#0f172a" strokeWidth="2" transform="rotate(300 32 32)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold num" style={{ color: ringColor(pct) }}>{pct == null ? "—" : `${Math.round(pct)}%`}</div>
      </div>
      <div className="mt-1 text-[11px] font-semibold leading-tight truncate w-full" title={meta.label}>{meta.label}</div>
      <div className="text-[10px] text-muted-foreground num leading-tight">{money(m.actual, meta.unit)} <span className="opacity-60">/ {money(m.mtd_target, meta.unit)}</span></div>
      <div className="text-[10px] text-muted-foreground num leading-tight">month {money(m.target, meta.unit)}</div>
    </div>
  );
}

function Pace({ rev, d, unit }) {
  const expected = d.working_days ? (d.elapsed / d.working_days) * 100 : 0;
  const done = rev.target ? Math.min((rev.actual / rev.target) * 100, 100) : 0;
  const ahead = rev.gap >= 0;
  const perDay = unit === "nos" ? "" : "/day";
  return (
    <div className="space-y-1.5" data-testid="target-pace">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold flex items-center gap-1">{ahead ? <TrendingUp size={14} className="text-emerald-700" /> : <TrendingDown size={14} className="text-red-600" />}{ahead ? "Ahead of pace" : "Behind pace"} by {money(Math.abs(rev.gap), unit)}</span>
        <span className="text-muted-foreground num">{rev.month_pct ?? 0}% of month · day {d.elapsed}/{d.working_days}</span>
      </div>
      <div className="relative h-3 rounded-full bg-secondary overflow-visible">
        <div className={`h-3 rounded-full ${ahead ? "bg-emerald-600" : "bg-amber-500"}`} style={{ width: `${done}%`, transition: "width 900ms cubic-bezier(.2,.8,.2,1)" }} />
        <div className="absolute -top-1 h-5 w-0.5 bg-foreground" style={{ left: `calc(${Math.min(expected, 100)}% - 1px)` }} title={`MTD pace marker: ${Math.round(expected)}% of month`} />
        <Flag size={12} className="absolute -top-4 right-0 text-foreground" />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div><div className="text-muted-foreground">Need per day</div><div className="font-semibold num">{rev.needed_per_day == null ? "—" : money(rev.needed_per_day, unit)} <span className="text-muted-foreground font-normal">× {d.remaining} days</span></div></div>
        <div><div className="text-muted-foreground">Run-rate</div><div className="font-semibold num">{money(rev.run_rate, unit)}{perDay} <span className="text-muted-foreground font-normal">vs {money(rev.daily_target, unit)}{unit === "nos" ? " a day" : ""}</span></div></div>
        <div><div className="text-muted-foreground">Projected month-end</div><div className={`font-semibold num ${rev.projected >= rev.target ? "text-emerald-700" : "text-red-600"}`}>{money(rev.projected, unit)} <span className="font-normal">({rev.target ? Math.round((rev.projected / rev.target) * 100) : 0}%)</span></div></div>
      </div>
    </div>
  );
}

function TodayStreak({ row, cfg }) {
  const t = row.today;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="target-today">
      <div className="rounded-md border bg-background p-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest day · {fmtDay(t.date)}</div>
        <div className={`text-lg font-extrabold num ${t.pct == null ? "" : t.pct >= 100 ? "text-emerald-700" : "text-red-600"}`}>{t.pct == null ? "—" : `${Math.round(t.pct)}%`}</div>
        <div className="text-[10px] text-muted-foreground num">{money(t[cfg.headline], cfg.unit)} of {money(t.target, cfg.unit)} daily target</div>
      </div>
      <div className="rounded-md border bg-background p-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Flame size={11} className={row.streak ? "text-orange-500" : ""} /> Streak</div>
        <div className="text-lg font-extrabold num">{row.streak}<span className="text-xs font-semibold text-muted-foreground"> day{row.streak === 1 ? "" : "s"}</span></div>
        <div className="text-[10px] text-muted-foreground num">daily target hit {row.hits} of {row.days} days</div>
      </div>
    </div>
  );
}

export function Race({ race, myCodes = [], title = "Race to target", linkBase }) {
  if (!race?.length) return null;
  return (
    <div className="space-y-1.5" data-testid="target-race">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider font-semibold text-muted-foreground"><span className="flex items-center gap-1"><Car size={13} /> {title}</span><span className="flex items-center gap-1"><Flag size={12} /> finish = 100% of MTD target</span></div>
      <ol className="space-y-1">
        {race.map((r, i) => {
          const pct = r.pct ?? 0, pos = Math.min(pct, 120) / 120 * 100, mine = myCodes.includes(r.code);
          const name = <>{r.code} <span className="font-normal text-muted-foreground">{r.name}</span></>;
          return (
            <li key={r.code} className={`flex items-center gap-2 text-xs ${mine ? "font-bold" : ""}`} data-testid={`race-row-${r.code}`}>
              <span className="w-4 text-right text-muted-foreground num">{i + 1}</span>
              {linkBase ? <Link to={`${linkBase}/${r.code}`} className={`w-28 truncate hover:underline ${mine ? "text-amber-900" : ""}`}>{name}</Link> : <span className={`w-28 truncate ${mine ? "text-amber-900" : ""}`}>{name}</span>}
              <div className="relative flex-1 h-5">
                <div className="absolute inset-y-2 left-0 right-0 rounded-full bg-secondary" />
                <div className="absolute inset-y-0 w-0.5 bg-foreground/70" style={{ left: `${100 / 120 * 100}%` }} />
                <div className="absolute top-0 -ml-2.5 transition-[left] duration-700" style={{ left: `${pos}%`, color: ringColor(r.pct) }}><Car size={20} fill={mine ? "#fbbf24" : "none"} /></div>
              </div>
              <span className="w-12 text-right num" style={{ color: ringColor(r.pct) }}>{r.pct == null ? "—" : `${Math.round(r.pct)}%`}</span>
              <span className="w-16 hidden sm:block">{r.tier ? <TierChip tier={r.tier} /> : <span className="text-[9px] text-muted-foreground">below 80%</span>}</span>
              {r.streak > 0 && <span className="hidden sm:flex items-center text-[10px] text-orange-600 num"><Flame size={11} />{r.streak}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function useCelebration(d, key, cfg) {
  useEffect(() => {
    if (!d?.has_targets) return;
    const crossed = d.rows.filter((r) => r.crossed_100);
    if (!crossed.length) return;
    const store = `${cfg.store}:${d.month}:${d.cat || "all"}:${key}`;
    const seen = new Set(JSON.parse(localStorage.getItem(store) || "[]"));
    const fresh = crossed.filter((r) => !seen.has(r.code));
    if (!fresh.length) return;
    confetti({ particleCount: 160, spread: 80, origin: { y: 0.3 }, colors: ["#d97706", "#7c3aed", "#059669", "#0f172a"] });
    toast.success(`${fresh.map((r) => `${r.code} ${r.name}`).join(", ")} crossed 100% of the ${cfg.celebrate(d)} — congratulations!`, { duration: 8000 });
    localStorage.setItem(store, JSON.stringify([...seen, ...fresh.map((r) => r.code)]));
  }, [d, key, cfg]);
}

function AchievementPanel({ cfg, query, storeKey, myCodes = [], showRace = false, canManage = false, defaultCat = "GS", raceLink }) {
  const [cat, setCat] = useState(defaultCat);
  const [d, setD] = useState(null);
  const params = qs(cfg.cats ? { ...query, cat } : query);
  useEffect(() => {
    setD(null);
    api.get(`${cfg.endpoint}?${params}`).then(({ data }) => setD(data)).catch((e) => { toast.error(apiError(e), { id: "target-load" }); setD(false); });
  }, [cfg.endpoint, params]);
  useCelebration(d, storeKey, cfg);
  const metaByKey = useMemo(() => Object.fromEntries((d?.metrics || []).map((m) => [m.key, m])), [d]);
  const showManage = !cfg.manage.adminOnly || canManage;
  const ManageIcon = cfg.manage.icon;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><Target size={14} /> Target achievement {d?.has_targets && d.cat_label && <span className="text-muted-foreground font-normal">· {d.cat_label}</span>}</h3>
      <div className="flex items-center gap-2">
        {d?.has_targets && <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="target-calendar"><CalendarDays size={12} /> {d.working_days} working days · {d.elapsed} elapsed · {d.remaining} left · as of {fmtDay(d.as_of)}</span>}
        {d?.has_targets && cfg.cats && <span className={`text-[10px] rounded px-1.5 py-0.5 font-semibold ${d.ex_warranty ? "bg-secondary text-muted-foreground" : "bg-emerald-50 text-emerald-800"}`} data-testid="target-basis">{d.ex_warranty ? "ex-warranty" : "incl. warranty"}</span>}
        {cfg.cats && (
          <div className="flex items-center rounded-md border overflow-hidden text-[11px]" data-testid="target-cats">
            {(d?.cats || [{ key: "GS" }, { key: "BS" }, { key: "TOTAL" }]).map((c) => <button key={c.key} onClick={() => setCat(c.key)} className={`px-2 py-1 font-semibold ${cat === c.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`target-cat-${c.key}`}>{c.key === "TOTAL" ? "Total" : c.key}</button>)}
          </div>
        )}
      </div>
    </div>
  );

  if (d === null) return <div className="border bg-card rounded-md p-4 space-y-3" data-testid="target-panel">{header}<div className="h-24 animate-pulse rounded bg-secondary" /></div>;
  if (d === false) return null;
  if (!d.has_targets) {
    return (
      <div className="border bg-card rounded-md p-4 space-y-2" data-testid="target-panel">
        {header}
        <p className="text-xs text-muted-foreground" data-testid="target-empty">{cfg.empty(d, cat, !!(query.workshop || query.dealer))}{showManage && <> <Link to={cfg.manage.to} className="underline inline-flex items-center gap-1"><ManageIcon size={11} /> {cfg.manage.label}</Link></>}</p>
      </div>
    );
  }
  const t = d.total;
  const headPct = t[`${cfg.headline}_pct`];
  return (
    <div className="border bg-card rounded-md p-4 space-y-4" data-testid="target-panel">
      {header}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl font-extrabold num" style={{ color: ringColor(headPct) }} data-testid="target-headline-pct">{headPct == null ? "—" : `${Math.round(headPct)}%`}</span>
        <span className="text-xs text-muted-foreground">of MTD {cfg.headlineLabel} · {t.name}</span>
        <TierChip tier={t.tier} size="md" />
        {!t.tier && <span className="text-[10px] text-muted-foreground">Bronze from 80% · Silver 90% · Gold 100% · Platinum 110%</span>}
        {d.unplanned?.length > 0 && <span className="text-[10px] text-muted-foreground ml-auto" data-testid="target-unplanned">{d.unplanned.length} dealer{d.unplanned.length === 1 ? "" : "s"} without a plan: {d.unplanned.join(", ")}</span>}
        {d.prorated?.length > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" title={PRORATED_TITLE} data-testid="target-prorated"><ProratedBadge count={d.prorated.length} testId="target-prorated-badge" /> {d.prorated.join(", ")}</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="target-rings">
        {d.metrics.filter((m) => t.metrics[m.key]).map((m) => <Ring key={m.key} m={t.metrics[m.key]} meta={metaByKey[m.key]} />)}
      </div>
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">{t.metrics[cfg.headline] && <Pace rev={t.metrics[cfg.headline]} d={d} unit={cfg.unit} />}</div>
        <div className="lg:col-span-2"><TodayStreak row={t} cfg={cfg} /></div>
      </div>
      {showRace && d.rows.length > 1 && <Race race={d.race} myCodes={myCodes} linkBase={raceLink} />}
    </div>
  );
}

export function TargetPanel({ month, workshop, myCodes = [], showRace = false, canManage = false, defaultCat = "GS", exWarranty = false }) {
  return <AchievementPanel cfg={SERVICE_CFG} query={{ month, workshop, ex_warranty: exWarranty }} storeKey={workshop || "scope"} myCodes={myCodes} showRace={showRace} canManage={canManage} defaultCat={defaultCat} raceLink="/service/workshop" />;
}

export function SalesTargetPanel({ month, dealer, myCodes = [], showRace = false }) {
  return <AchievementPanel cfg={SALES_CFG} query={{ month, dealer }} storeKey={dealer || "scope"} myCodes={myCodes} showRace={showRace} canManage raceLink="/dpc/dealer" />;
}
